// /requisitions/:id: where one request is, who moved it, and the one thing the
// caller can do next. Every button comes from the server's `can` block; the
// page never works out a permission for itself.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import ReasonPopover from './ReasonPopover';
import RequisitionForm, { type SaveIntent } from './RequisitionForm';
import RequisitionTimeline from './RequisitionTimeline';
import StatusChip, { UrgentTag } from './StatusChip';
import StepsStrip from './StepsStrip';
import PageLayout from '../components/ui/PageLayout';
import RequestFormHelp from './RequestFormHelp';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { Panel } from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import FadeIn from '../components/ui/FadeIn';
import MarkBoughtPopover from './MarkBoughtPopover';
import PackagesCard from './PackagesCard';
import StockCard, { partAsRow, type StockDraft } from './StockCard';
import ReceiptsCard from './ReceiptsCard';
import {
  URGENCY_LABELS,
  centsToDollars,
  errorMessage,
  formatCents,
  formatQuantity,
  toNumber,
  useRequisitionsApi,
  type DraftInput,
  type RequisitionDetail,
  type RequisitionLine,
} from './requisitionsApi';

type ActionKey =
  | 'submit'
  | 'approve'
  | 'deny'
  | 'cancel'
  | 'mark_bought'
  | 'close'
  | 'mark_arrived';

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const lineTotal = (l: RequisitionLine): number | null => {
  const cost = toNumber(l.estimated_unit_cost_cents);
  const qty = toNumber(l.quantity);
  return cost === null || qty === null ? null : Math.round(cost * qty);
};

const LINE_COLUMNS: ColumnDef<RequisitionLine, any>[] = [
  {
    id: 'description',
    header: 'Item',
    meta: { cellClass: 'min-w-[12rem]' },
    cell: ({ row }) => {
      const l = row.original;
      return (
        <div className="min-w-0">
          <div className="break-words text-on-surface">{l.description}</div>
          {(l.link_url || l.notes) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-on-surface-variant">
              {l.link_url && (
                <a
                  href={l.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-[16rem] items-center gap-1 text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                >
                  <span className="truncate">{hostOf(l.link_url)}</span>
                  <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3 w-3 shrink-0">
                    <path fill="currentColor" d="M9 2.75A.75.75 0 0 1 9.75 2h3.5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V4.56L8.28 8.78a.75.75 0 0 1-1.06-1.06l4.22-4.22H9.75A.75.75 0 0 1 9 2.75ZM3.5 5.25c0-.41.34-.75.75-.75H6.5a.75.75 0 0 0 0-1.5H4.25A2.25 2.25 0 0 0 2 5.25v6.5A2.25 2.25 0 0 0 4.25 14h6.5A2.25 2.25 0 0 0 13 11.75V9.5a.75.75 0 0 0-1.5 0v2.25c0 .41-.34.75-.75.75h-6.5a.75.75 0 0 1-.75-.75v-6.5Z" />
                  </svg>
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}
              {l.notes && <span className="break-words">{l.notes}</span>}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => formatQuantity(row.original.quantity),
  },
  {
    id: 'store',
    header: 'Store',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => row.original.store || '—',
  },
  {
    id: 'each',
    header: 'Each',
    meta: { align: 'right', numeric: true, cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => formatCents(row.original.estimated_unit_cost_cents),
  },
  {
    id: 'total',
    header: 'Total',
    meta: {
      align: 'right',
      numeric: true,
      cellClass: 'whitespace-nowrap text-on-surface',
      csv: (l) => centsToDollars(lineTotal(l)),
    },
    cell: ({ row }) => formatCents(lineTotal(row.original)),
  },
];

const lineRowId = (l: RequisitionLine) => l.id;

/** The latest deny, if nothing has been sent since. */
function openDenial(r: RequisitionDetail) {
  if (r.status !== 'draft') return null;
  const ordered = [...r.events].sort((a, b) => a.created_at.localeCompare(b.created_at));
  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    if (ordered[i].action === 'submitted') return null;
    if (ordered[i].action === 'denied') return ordered[i];
  }
  return null;
}

/** One sentence for the decision callout: where it stands, from the reader's side. */
function standing(r: RequisitionDetail): { head: string; sub: string | null } {
  const c = r.can;
  switch (r.status) {
    case 'draft': {
      const d = openDenial(r);
      if (d) {
        return {
          head: `Denied by ${d.actor?.name || 'an approver'}`,
          sub: d.reason
            ? `“${d.reason}” Change what is needed and send it again, or cancel it.`
            : 'Change what is needed and send it again, or cancel it.',
        };
      }
      return { head: 'This is a draft', sub: 'Nobody else sees it until you send it for approval.' };
    }
    case 'pending_approval':
      return c.approve
        ? { head: 'Waiting for your approval', sub: `${r.requester?.name || 'Someone'} is asking for this.` }
        : { head: 'Waiting for an approver', sub: 'You hear in Slack or by email as soon as they decide.' };
    case 'approved':
      return c.mark_bought
        ? { head: 'Approved and ready to buy', sub: r.approved_by ? `Approved by ${r.approved_by.name}.` : null }
        : { head: 'Approved', sub: 'A buyer orders it next. You hear when it is bought.' };
    case 'bought':
      if (r.lines.some((l) => l.needs_catalog_item)) {
        return {
          head: 'Waiting on a catalog item',
          sub: c.set_catalog
            ? 'Pick the catalog item for each line going into stock, then it can be marked arrived.'
            : 'A buyer picks the catalog item for a line going into stock, then it can be marked arrived.',
        };
      }
      if (r.shipments.length > 0 && r.shipments.every((s) => s.arrived_at)) {
        return {
          head: 'Every package is in',
          sub: c.mark_arrived ? 'Mark it arrived to close it out.' : 'You hear when it is marked arrived.',
        };
      }
      return {
        head: 'Bought, on the way',
        sub: c.mark_arrived
          ? 'Mark it arrived once it is here.'
          : 'You hear when it arrives.',
      };
    case 'arrived':
      return {
        head: 'Arrived',
        sub: [r.arrived_by?.name, r.arrived_at ? formatTimestamp(r.arrived_at, 'MMM D') : null]
          .filter(Boolean)
          .join(', ') || null,
      };
    case 'closed':
      return { head: "Won't buy", sub: r.close_reason ? `“${r.close_reason}”` : null };
    case 'cancelled':
      return { head: 'Cancelled', sub: null };
    default:
      return { head: '', sub: null };
  }
}

const DetailHelp: React.FC = () => (
  <>
    <p>
      This page shows one request: where it is, who moved it there, and what you can do with it
      now. The buttons you see are the ones you are allowed to press.
    </p>
    <p className="font-medium text-on-surface">The steps</p>
    <p>
      Asked, Approved, Bought, Arrived. The strip at the top fills in as each happens, with who
      did it and when.
    </p>
    <p className="font-medium text-on-surface">Approving</p>
    <p>
      Approve sends it to the buyers. Deny sends it back to the person who asked, as a draft,
      with your reason. You cannot approve your own request unless you are the only approver.
    </p>
    <p className="font-medium text-on-surface">Buying</p>
    <p>
      Mark bought once it is ordered, and pick the card receipt if it is already uploaded. If
      not, choose Receipt to follow: the request waits under Receipt missing until someone links
      it from the Receipts card, or from the receipt itself. Won&apos;t buy ends it, with a reason
      that goes to the person who asked.
    </p>
    <p className="font-medium text-on-surface">Packages</p>
    <p>
      The buyer adds each tracking number, and the carrier is worked out from it. Receiving scans
      the label when a box lands, which finds the request without anyone typing.
    </p>
    <p className="font-medium text-on-surface">Into stock</p>
    <p>
      Tick Add to inventory on anything that should go on the shelf as stock. A buyer picks the
      catalog item it is. Once it is here, Receive into stock books it in with its cost, and the
      item shows In stock with a link to its box.
    </p>
    <p className="font-medium text-on-surface">Arrived</p>
    <p>
      Whoever has it in hand marks it arrived, with a note on where it is if that helps. With
      several packages, each one is marked on its own and the last one arrives the request. The
      person who asked hears about it.
    </p>
  </>
);

const RequisitionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reqApi = useRequisitionsApi();
  const toast = useToast();

  const [req, setReq] = useState<RequisitionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<'not_found' | 'error' | 'stocked' | null>(null);
  // The request number to name once receiving loses sight of a request it finished stocking.
  const [stockedAs, setStockedAs] = useState<string | null>(null);
  const [busy, setBusy] = useState<ActionKey | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState<SaveIntent | null>(null);
  const [stockDraft, setStockDraft] = useState<Record<string, StockDraft>>({});

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setReq(await reqApi.get(id));
      setFailure(null);
    } catch (err) {
      const gone = (err as { status?: number })?.status === 404;
      if (!quiet) setFailure(gone ? 'not_found' : 'error');
      else if (gone) {
        // Receiving sees a request only while something on it is left to stock,
        // so a 404 after stocking means the work is done, not that it vanished.
        const wasStocking =
          !!req && ['bought', 'arrived'].includes(req.status) && req.lines.some((l) => l.add_to_inventory);
        setStockedAs(wasStocking && req ? req.req_number : null);
        setFailure(wasStocking ? 'stocked' : 'not_found');
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    setEditing(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The approver's stock choices start from what the request holds, and start
  // over when a new version is sent.
  useEffect(() => {
    if (!req) return;
    const next: Record<string, StockDraft> = {};
    req.lines.forEach((l) => {
      next[l.id] = { on: l.add_to_inventory, part: l.part ? partAsRow(l.part) : null };
    });
    setStockDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req?.id, req?.submitted_at]);

  /** Runs one action. Resolves true when it landed, for the popovers to close. */
  const run = async (
    key: ActionKey,
    call: () => Promise<RequisitionDetail>,
    success: (r: RequisitionDetail) => string
  ): Promise<boolean> => {
    setBusy(key);
    try {
      const updated = await call();
      setReq(updated);
      toast.success(success(updated));
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'That did not go through. Check your connection and try again.'));
      // Someone else may have moved it; the buttons should match what is true now.
      load(true);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async (draft: DraftInput, intent: SaveIntent) => {
    if (!req) return;
    setSaving(intent);
    try {
      let updated = await reqApi.update(req.id, draft);
      if (intent === 'send') {
        try {
          updated = await reqApi.submit(updated.id);
          toast.success(`Sent ${updated.req_number} for approval.`);
        } catch (err) {
          toast.error(`Saved, but not sent. ${errorMessage(err, 'Try Send for approval again.')}`);
        }
      } else {
        toast.success('Saved.');
      }
      setReq(updated);
      setEditing(false);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save your changes. Check your connection and try again.'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <PageLayout kind="workspace" className="pt-4">
        <LogoSpinner label="Loading request" />
      </PageLayout>
    );
  }

  if (failure === 'stocked') {
    return (
      <PageLayout kind="workspace" className="pt-4 space-y-5">
        <PageHeader title="Request" back={{ to: "/receiving?tab=requests", label: "Receiving" }} />
        <EmptyState className="max-w-2xl" title={<>Everything on {stockedAs || 'this request'} is in stock</>}>
          Nothing on it is left for receiving, so it has left your list.
        </EmptyState>
      </PageLayout>
    );
  }

  if (failure || !req) {
    const notFound = failure === 'not_found';
    return (
      <PageLayout kind="workspace" className="pt-4 space-y-5">
        <PageHeader title="Request" back={{ to: '/requisitions', label: 'Requests' }} />
        <EmptyState
          className="max-w-2xl"
          title={notFound ? 'This request is not here' : 'Could not load this request'}
          action={
            <>
              {!notFound && (
                <button type="button" onClick={() => load()} className={buttonClass('quiet')}>
                  Try again
                </button>
              )}
              <Link to="/requisitions" className={buttonClass('quiet')}>
                All requests
              </Link>
            </>
          }
        >
          {notFound
            ? 'It may have been mistyped, or it belongs to someone else. Requests are visible to the person who asked, approvers and buyers.'
            : 'Check your connection, then try again.'}
        </EmptyState>
      </PageLayout>
    );
  }

  const c = req.can;
  const anyAction = c.submit || c.approve || c.deny || c.mark_bought || c.close || c.mark_arrived;
  const { head, sub } = standing(req);
  const who = req.requester?.name || 'The person who asked';

  const header = (
    <PageHeader
      back={{ to: '/requisitions', label: 'Requests' }}
      title={req.purpose}
      helpTitle="About this request"
      help={editing ? <RequestFormHelp /> : <DetailHelp />}
      meta={
        <>
          <Tag variant="code" mono>{req.req_number}</Tag>
          <StatusChip status={req.status} />
          <UrgentTag urgency={req.urgency} />
        </>
      }
      actions={
        <SecondaryAction variant="text" to="/requisitions">
          All requests
        </SecondaryAction>
      }
    />
  );

  if (editing) {
    return (
      <PageLayout kind="workspace" className="pt-4 space-y-5">
        {header}
        <div className="max-w-4xl">
          <RequisitionForm
            initial={req}
            saving={saving}
            onSave={saveEdit}
            onCancel={() => setEditing(false)}
            cancelLabel="Stop editing"
            wasPending={req.status === 'pending_approval'}
          />
        </div>
      </PageLayout>
    );
  }

  // Held until Approve only for someone who can approve; otherwise each change saves.
  const drafting = req.status === 'pending_approval' && !!c.set_inventory && !!c.approve;

  /** Approve, carrying the stock choices. Lines unticked here are cleared first. */
  const approve = async (): Promise<RequisitionDetail> => {
    if (!drafting) return reqApi.approve(req.id, req.submitted_at);
    for (const l of req.lines) {
      if (l.add_to_inventory && !stockDraft[l.id]?.on) {
        await reqApi.setLineInventory(req.id, l.id, { add_to_inventory: false });
      }
    }
    const inventoryLines = req.lines
      .filter((l) => stockDraft[l.id]?.on)
      .map((l) => {
        const part = stockDraft[l.id].part;
        return { line_id: l.id, ...(c.set_catalog && part ? { part_catalog_id: part.id } : {}) };
      });
    return inventoryLines.length
      ? reqApi.approve(req.id, req.submitted_at, inventoryLines)
      : reqApi.approve(req.id, req.submitted_at);
  };

  const estimate = toNumber(req.estimated_total_cents);
  const lines = [...req.lines].sort((a, b) => a.line_number - b.line_number);

  return (
    <PageLayout
      kind="record"
      className="pt-4"
      header={header}
      rail={(
        <>
          <Panel title="Details">
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
              <dt className="text-on-surface-variant">Asked by</dt>
              <dd className="text-on-surface">{req.requester?.name || '—'}</dd>
              <dt className="text-on-surface-variant">Needed by</dt>
              <dd className="tabular-nums text-on-surface">
                {req.needed_by ? formatEventDate(req.needed_by) : 'Any time'}
              </dd>
              <dt className="text-on-surface-variant">Urgency</dt>
              <dd className="text-on-surface">{URGENCY_LABELS[req.urgency]}</dd>
              <dt className="text-on-surface-variant">Bought for</dt>
              <dd className="text-on-surface">{req.purchase_class?.name || 'Not sure'}</dd>
              <dt className="text-on-surface-variant">Show</dt>
              <dd className="break-words text-on-surface">{req.event?.name || 'None'}</dd>
              <dt className="text-on-surface-variant">Started</dt>
              <dd className="tabular-nums text-on-surface">{formatTimestamp(req.created_at)}</dd>
            </dl>
          </Panel>

          {req.receipts && (
            <ReceiptsCard requisition={req} receipts={req.receipts} onUpdated={setReq} />
          )}

          <Panel title="History">
            <RequisitionTimeline events={req.events} />
          </Panel>
        </>
      )}
    >
      <StepsStrip requisition={req} />

      {/* The decision: the page's one elevated element, ringed when the reader
          is the one who moves it next. */}
      <Panel
        label="What happens next"
        on={anyAction ? 'panel' : 'ground'}
        className={anyAction ? 'ring-1 ring-primary/30' : ''}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <FadeIn key={head} className="min-w-0">
            <h2 className="text-base font-semibold text-on-surface">{head}</h2>
            {sub && <p className="mt-0.5 break-words text-sm text-on-surface-variant">{sub}</p>}
          </FadeIn>
          <FadeIn key={req.status} className="flex flex-wrap items-center gap-2">
            {c.submit && (
              <Tooltip label="Tells the approvers in Slack and by email">
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() =>
                    run('submit', () => reqApi.submit(req.id), (r) => `Sent ${r.req_number} for approval.`)
                  }
                  className={buttonClass('filled')}
                >
                  {busy === 'submit' ? 'Sending…' : 'Send for approval'}
                </button>
              </Tooltip>
            )}
            {c.approve && (
              <Tooltip label="Approves it and tells the buyers it is ready">
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() =>
                    run('approve', approve, (r) => `Approved ${r.req_number}. The buyers have been told.`)
                  }
                  className={buttonClass('filled')}
                >
                  {busy === 'approve' ? 'Approving…' : 'Approve'}
                </button>
              </Tooltip>
            )}
            {c.mark_bought && (
              <MarkBoughtPopover
                requisitionId={req.id}
                busy={busy === 'mark_bought'}
                disabled={busy !== null}
                onConfirm={(pids) =>
                  run(
                    'mark_bought',
                    () => reqApi.markBought(req.id, pids || undefined),
                    () =>
                      pids
                        ? 'Marked bought, with the receipt linked.'
                        : 'Marked bought. It waits under Receipt missing until the receipt is linked.'
                  )
                }
              />
            )}
            {c.mark_arrived && (
              <ReasonPopover
                triggerLabel="Mark arrived"
                triggerClassName={buttonClass('filled')}
                tooltip="Marks it arrived and tells the person who asked"
                title="Mark this request arrived"
                fieldLabel="Where is it? (optional)"
                placeholder="On your desk, or the shelf by receiving"
                required={false}
                maxLength={500}
                confirmLabel="Mark arrived"
                confirmTone="primary"
                busy={busy === 'mark_arrived'}
                disabled={busy !== null}
                onConfirm={(note) =>
                  run('mark_arrived', () => reqApi.markArrived(req.id, note), () => 'Marked arrived.')
                }
              />
            )}
            {c.edit && (
              <Tooltip label="Change what is asked for">
                <button type="button" disabled={busy !== null} onClick={() => setEditing(true)} className={buttonClass('quiet')}>
                  Edit
                </button>
              </Tooltip>
            )}
            {c.deny && (
              <ReasonPopover
                triggerLabel="Deny"
                triggerClassName={buttonClass('danger')}
                tooltip="Sends it back to the person who asked, with your reason"
                title="Deny this request"
                fieldLabel={`Why? ${who} sees this.`}
                placeholder="We already have two of these in the shop"
                required
                maxLength={1000}
                confirmLabel="Deny"
                confirmTone="danger"
                hint="It goes back to them as a draft they can fix and send again."
                busy={busy === 'deny'}
                disabled={busy !== null}
                onConfirm={(reason) =>
                  run('deny', () => reqApi.deny(req.id, reason, req.submitted_at), () => `Denied. ${who} gets your reason.`)
                }
              />
            )}
            {c.close && (
              <ReasonPopover
                triggerLabel="Won't buy"
                triggerClassName={buttonClass('danger')}
                tooltip="Ends the request without buying it, with a reason"
                title="Won't buy this"
                fieldLabel={`Why not? ${who} sees this.`}
                placeholder="Out of stock everywhere; try again next month"
                required
                maxLength={1000}
                confirmLabel="Won't buy"
                confirmTone="danger"
                busy={busy === 'close'}
                disabled={busy !== null}
                onConfirm={(reason) =>
                  run('close', () => reqApi.close(req.id, reason), () => `Marked won't buy. ${who} gets your reason.`)
                }
              />
            )}
            {c.cancel && (
              <ReasonPopover
                triggerLabel="Cancel request"
                triggerClassName={buttonClass('quiet')}
                tooltip="Withdraws the request. It cannot be sent again."
                title="Cancel this request"
                fieldLabel="Why? (optional)"
                required={false}
                maxLength={1000}
                confirmLabel="Cancel request"
                confirmTone="danger"
                busy={busy === 'cancel'}
                disabled={busy !== null}
                onConfirm={(reason) =>
                  run('cancel', () => reqApi.cancel(req.id, reason), (r) => `Cancelled ${r.req_number}.`)
                }
              />
            )}
          </FadeIn>
        </div>
      </Panel>

      <section aria-labelledby="req-items" className="min-w-0 space-y-2">
        <h2 id="req-items" className="text-base font-semibold text-on-surface">
          Items
        </h2>
        <DataTable<RequisitionLine>
          data={lines}
          columns={LINE_COLUMNS}
          getRowId={lineRowId}
          empty={
            <EmptyState variant="area">
              No items yet.{c.edit ? ' Edit the request to add what you need.' : ''}
            </EmptyState>
          }
          footer={
            <span className="ml-auto text-sm text-on-surface-variant">
              {estimate === null ? (
                'No prices given'
              ) : (
                <>
                  About <span className="font-semibold tabular-nums text-on-surface">{formatCents(estimate)}</span>{' '}
                  across {lines.length} {lines.length === 1 ? 'item' : 'items'}
                </>
              )}
            </span>
          }
        />

        <StockCard
          requisition={req}
          draft={drafting ? stockDraft : null}
          onDraftChange={(lineId, next) => setStockDraft((d) => ({ ...d, [lineId]: next }))}
          onUpdated={setReq}
          onReload={() => load(true)}
        />

        <PackagesCard requisition={req} onUpdated={setReq} />
      </section>
    </PageLayout>
  );
};

export default RequisitionDetailPage;
